/**
 * Class lists shared by parts that must look identical across components.
 *
 * A select trigger and a text input are the same control to the eye; a menu
 * popup and a combobox popup are the same sheet of paper. Keeping those skins
 * in one place is what stops the twelve components below from drifting apart
 * one `px-3` at a time.
 *
 * These are literal strings, not assembled at runtime, so Tailwind's source
 * scanner still sees every utility. See lib/scale.ts for why that matters.
 */

/**
 * Text-like controls: Input, Field.Control, Combobox.Input, NumberField.Input.
 *
 * `bg-surface` is deliberate and is the detail that makes a Vendy form look
 * like one: the field is *lighter than the card it sits on*, rather than white
 * on white with a border doing all the work.
 */
export const controlSurface =
  'h-11 w-full rounded-control border border-border-subtle bg-surface px-4 text-md text-ink ' +
  'placeholder:text-ink-faint ' +
  'focus-visible:border-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/35 ' +
  'data-invalid:border-danger data-invalid:ring-2 data-invalid:ring-danger/20 ' +
  'disabled:cursor-not-allowed disabled:bg-surface-muted disabled:text-ink-faint ' +
  'data-disabled:cursor-not-allowed data-disabled:bg-surface-muted data-disabled:text-ink-faint';

/**
 * The same control, taken apart, for the cases that put something *inside* the
 * field — a trailing button, a prefix, a unit. The shell carries the border,
 * fill and focus ring; the input inside it is bare. Together they measure the
 * same as `controlSurface` (6px shell padding + 10px input padding = its px-4).
 *
 * Invalid and disabled are read off `Field.Root`, which Base UI marks, so they
 * reach the shell rather than the transparent input nobody can see.
 */
export const controlShell =
  'flex h-11 w-full items-center rounded-control border border-border-subtle bg-surface px-1.5 ' +
  'focus-within:border-brand-400 focus-within:ring-2 focus-within:ring-brand-400/35 ' +
  'group-data-invalid:border-danger group-data-invalid:ring-2 group-data-invalid:ring-danger/20 ' +
  'group-data-disabled:cursor-not-allowed group-data-disabled:bg-surface-muted';

export const controlShellInput =
  'h-full w-full min-w-0 bg-transparent px-2.5 text-md text-ink outline-none ' +
  'placeholder:text-ink-faint disabled:cursor-not-allowed disabled:text-ink-faint';

/**
 * The three pieces of text around a control. Vendy's label is heavier and
 * larger than the usual — bold 15px, the same size as the value it labels —
 * and the hint below is a step down rather than a whisper.
 */
export const fieldLabel = 'text-md font-semibold text-ink data-disabled:text-ink-faint';
/** "по желанию" — the optional marker sits inside the label, unemphasised. */
export const fieldOptional = 'ml-1.5 text-sm font-normal text-ink-muted';
export const fieldHint = 'text-sm text-ink-muted';
export const fieldError = 'text-sm text-danger-text';

/** Floating sheets: Menu, Select, Combobox, Popover, NavigationMenu popups. */
export const popupSurface =
  'rounded-card border border-border-subtle bg-surface-raised text-ink shadow-popup outline-none';

/**
 * The enter/exit transition every anchored popup shares, driven by the
 * `data-starting-style` / `data-ending-style` attributes Base UI sets around
 * mount and unmount, and originating from the side it opens towards.
 */
export const popupTransition =
  'origin-[var(--transform-origin)] transition-[opacity,transform] duration-150 ease-out-quint ' +
  'data-starting-style:scale-95 data-starting-style:opacity-0 ' +
  'data-ending-style:scale-95 data-ending-style:opacity-0';

/**
 * Rows inside a popup: Menu.Item, Select.Item, Combobox.Item.
 *
 * Vendy's rows are generous — 46px tall, 16px of side padding, 15px text — and
 * that roominess is most of why their menus read as theirs.
 */
export const itemSurface =
  'flex cursor-default items-center gap-3 rounded-sm px-4 py-3 text-md text-ink outline-none select-none ' +
  'data-highlighted:bg-surface-muted data-highlighted:text-ink ' +
  'data-disabled:pointer-events-none data-disabled:text-ink-faint';

/**
 * A row in a menu that *expresses a choice* rather than performing an action —
 * Select, Combobox, Autocomplete, a filter facet.
 *
 * The product marks these two different ways, and the difference is meaningful:
 * an actions menu marks its current row with `surface-sunken` and weight, while
 * a menu you are choosing *from* marks it with the brand tint, the same way an
 * active nav item and a selected option card are marked. The tick comes along
 * for free wherever Base UI has an indicator.
 */
export const itemChoice =
  'data-selected:bg-brand-200 data-selected:font-semibold data-selected:text-ink ' +
  'data-checked:bg-brand-200 data-checked:font-semibold data-checked:text-ink';

/** A row that destroys something. */
export const itemDanger =
  'text-danger-text data-highlighted:bg-danger-tint data-highlighted:text-danger-text';

/**
 * The label above a group of rows, and the hairline between groups. The label
 * borrows the product's section-heading idiom: mono, upper case, tracked out.
 */
export const groupLabelSurface =
  'px-4 py-2 font-mono text-3xs font-medium tracking-wider text-ink-faint uppercase';
export const popupSeparator = 'mx-4 my-2 h-px bg-border-subtle';

/**
 * The little triangle that points a popup at its anchor.
 *
 * Geometry only — it is a square pseudo-element rotated 45° and clipped by the
 * part, flipped by the `data-side` Base UI sets once the popup has been placed.
 * Each popup adds its own `before:bg-*` / `before:border-*` so the tip matches
 * the sheet it belongs to.
 */
export const popupArrow =
  'relative block h-1.5 w-3 overflow-clip ' +
  'data-[side=top]:bottom-[-6px] data-[side=top]:rotate-180 ' +
  'data-[side=bottom]:top-[-6px] ' +
  'data-[side=left]:right-[-9px] data-[side=left]:rotate-90 ' +
  'data-[side=right]:left-[-9px] data-[side=right]:-rotate-90 ' +
  "before:absolute before:bottom-0 before:left-1/2 before:content-[''] " +
  'before:size-[calc(6px*sqrt(2))] before:[transform:translate(-50%,50%)_rotate(45deg)]';
